<template>
  <WdsStack justify-space-between align-center class="tw-mb-5">
    <WdsInput
      v-model="search"
      :placeholder="$t('Search name, description...')"
      prefix-icon="search"
    />
    <WdsButton data-test="create-workflow-button" primary @click="show = true">
      {{ $t('Create workflow') }}
    </WdsButton>
  </WdsStack>

  <WdsStack v-if="loading" justify-center align-center direction-column gap-md>
    <div class="icon-outer">
      <div class="icon-inner">
        <WdsSpinner indeterminate />
      </div>
    </div>
    <WdsText large>
      {{ $t('Loading workflows...') }}
    </WdsText>
  </WdsStack>
  <WdsStack
    v-else-if="error && workflows.length === 0"
    justify-center
    align-center
    direction-column
    gap-md
  >
    <ContentLoadingErrorNotice />
  </WdsStack>
  <WdsStack v-else-if="workflows.length === 0" justify-center align-center direction-column gap-md>
    <div class="icon-outer">
      <div class="icon-inner">
        <WdsIcon name="flow" :size="20"></WdsIcon>
      </div>
    </div>
    <WdsText large>
      {{ $t('There are no existing workflows') }}
    </WdsText>
    <WdsText secondary>
      {{ $t('Workflows created by you or your team will be listed here') }}
    </WdsText>
  </WdsStack>
  <WdsGrid v-else data-test="workflow-cards" col-gap-lg row-gap-lg columns="repeat(2, 1fr)">
    <WorkflowCard
      v-for="w of workflows"
      :key="w.id"
      :workflow="w"
      :duplicating-workflow="duplicateWorkflowLoading"
      @edit="workflowId = w.id"
      @delete="startDeleteWorkflow"
      @duplicate="duplicateWorkflow"
    />
  </WdsGrid>
  <teleport to="body">
    <transition name="translate">
      <div v-if="workflowId" class="overlay">
        <EditorPage
          :workflow-id="workflowId"
          @close="workflowId = ''"
          @new-id="workflowId = $event"
        />
      </div>
    </transition>
    <WdsModal :title="$t('New workflow details')" :show="show" @cancel="show = false">
      <WorkflowMetaEditor
        @submitted=";((workflowId = $event?.id ?? ''), (show = false))"
        @cancel="show = false"
      />
    </WdsModal>
    <WorkflowDeleteModal
      :show="showDeleteModal"
      :deleting-workflow-running="deletingWorkflow"
      :workflow-title="selectedWorkflowName"
      @cancel="closeDeleteWorkflow"
      @delete="deleteWorkflowMutation"
    />
  </teleport>
</template>

<script lang="ts" setup>
import {
  WdsStack,
  WdsInput,
  WdsButton,
  WdsText,
  WdsGrid,
  WdsIcon,
  WdsSpinner,
  WdsModal,
} from '@worksome/wds-components'
import { computed, ref } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import WorkflowCard from '@/components/Workflows/Pages/Workflow/WorkflowCard.vue'
import EditorPage from '@/components/Workflows/Pages/Editor/EditorPage.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { convertToPrimitive, uuid } from '@/utils/tools'
import useQueryEncoder from '@/composables/useQueryEncoder'
import {
  WorkflowFragment,
  WorkflowsQuery,
} from '@/components/Workflows/Pages/Workflow/graphql/queries'
import { DeleteWorkflowMutation } from '@/components/Workflows/Pages/Workflow/graphql/mutations'
import WorkflowMetaEditor from '@/components/Workflows/Pages/Workflow/WorkflowMetaEditor.vue'
import WorkflowDeleteModal from '@/components/Workflows/Pages/Workflow/WorkflowDeleteModal.vue'
import { NetworkError } from '@/utils/notification'
import { graphql, useFragment } from '@/gql'
import { Workflow } from '@/components/Workflows/Pages/Workflow/workflow'
import { mapWorkflowToNodeTree } from '@/components/Workflows/utils'
import NodeTrigger from '@/components/Workflows/Pages/Editor/Nodes/NodeTrigger/NodeTrigger'
import Node from '@/components/Workflows/Pages/Editor/Nodes/Node'
import { ApprovalStatus } from '@/gql/graphql'
import ContentLoadingErrorNotice from '@/components/ContentLoadingErrorNotice.vue'

const show = useQueryEncoder('new-workflow', false, (v) => convertToPrimitive(v, Boolean as any))
const workflowId = useQueryEncoder('workflow-id', '')
const clientId = 'publicApiCustomFields'

const search = ref('')
const showDeleteModal = ref(false)
const selectedWorkflowId = ref(null)
const selectedWorkflowName = ref('')

const { result, loading, error } = useQuery(
  WorkflowsQuery,
  {
    account: [currentAccount.globalId],
    search: search.value,
    usersFirst: 10,
    usersPage: 1,
    first: 100,
    page: 1,
  },
  {
    clientId,
  },
)

const {
  mutate: deleteWorkflowMutation,
  loading: deletingWorkflow,
  onDone: onWorkflowDeleted,
  onError: onWorkflowDeleteError,
} = useMutation(DeleteWorkflowMutation, () => ({
  variables: {
    input: {
      id: selectedWorkflowId.value ?? '',
    },
  },
  clientId,
  refetchQueries: [
    {
      query: WorkflowsQuery,
      variables: {
        account: [currentAccount.globalId],
        search: search.value,
        usersFirst: 10,
        usersPage: 1,
        first: 100,
        page: 1,
      },
      clientId,
    },
  ],
}))

const workflows = computed(() => {
  if (!loading.value) {
    return (
      result.value?.approvals?.data.map((workflow) => useFragment(WorkflowFragment, workflow)) || []
    )
  }

  return []
})

function closeDeleteWorkflow() {
  showDeleteModal.value = false
  selectedWorkflowId.value = null
  selectedWorkflowName.value = ''
}

function startDeleteWorkflow({ id, name }) {
  showDeleteModal.value = true
  selectedWorkflowId.value = id
  selectedWorkflowName.value = name
}

const { mutate: duplicateWorkflowMutation, loading: duplicateWorkflowLoading } = useMutation(
  graphql(`
    mutation CreateWorkflow($input: CreateWorkflowInput!) {
      createWorkflow(input: $input) {
        id
      }
    }
  `),
  {
    clientId,
    refetchQueries: ['Workflows'],
  },
)
async function duplicateWorkflow(workflow: Workflow) {
  const tree: Node[] = []

  const root = new NodeTrigger(workflow, {
    key: uuid(),
  })

  tree.push(root)
  mapWorkflowToNodeTree(workflow, tree[0])

  // This output is slightly different for what is needed for creating a workflow, so we have to modify it a tiny bit
  // That's why we are using any here
  const input: any = root.compile()

  const rootInput = input.root.pop()
  if (rootInput) {
    rootInput.data.company = currentAccount.globalId
    rootInput.data.name = `${rootInput.data.name} - ${$t('Copy')}`
    rootInput.data.status = ApprovalStatus.Inactive
  }

  input.root = rootInput
  const copyWorkflow = await duplicateWorkflowMutation({ input })
  if (copyWorkflow) {
    workflowId.value = copyWorkflow?.data?.createWorkflow.id
  }
}

onWorkflowDeleted(() => closeDeleteWorkflow())

onWorkflowDeleteError(({ graphQLErrors, networkError }) => {
  let errorMessage = $t('Something went wrong, please try again')

  if (graphQLErrors) {
    errorMessage = graphQLErrors[0].message
  } else if (networkError) {
    errorMessage = networkError.message
  }

  new NetworkError({
    msg: errorMessage,
  }).dispatch()

  closeDeleteWorkflow()
})
</script>

<style lang="scss" scoped>

.overlay {
  --greybackground: rgb(255, 255, 255);

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  transform: translateX(0%);
  z-index: 10;

  background-image: radial-gradient(
    ellipse 2px 2px,
    #ccc0fb,
    #ccc0fb 25%,
    var(--greybackground) 25%
  );
  background-size: 11px 11px;
  background-position: center;
}

.icon-outer {
  border-radius: 50%;
  @apply tw-bg-gray-50;
  padding: 6px;
  .icon-inner {
    padding: 7px;
    border-radius: 50%;
    @apply tw-bg-gray-300;
  }
}

.translate-enter-active,
.translate-leave-active {
  transition: all 0.5s;
}
.translate-enter-from,
.translate-leave-to {
  transform: translateX(100%);
}
</style>
